import { useState, useEffect, useMemo, Fragment } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import SafetyShell from "../safety/SafetyShell";
import {
  Boxes,
  RefreshCw,
  Settings2,
  Search,
  TrendingUp,
  TrendingDown,
  Package,
  AlertCircle,
  ArrowRight,
  Link2,
  Wand2,
  ChevronRight,
  ChevronDown,
  FileText,
  FileSpreadsheet,
  Loader2,
  Download,
  Truck,
  ClipboardList,
  DollarSign,
  IndianRupee,
  Eye,
  EyeOff,
  Scale,
  ScanLine,
} from "lucide-react";

const VALUE_MASK = "••••••";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const fmt = (n) => new Intl.NumberFormat().format(n || 0);

// Whole-number money formatters. FX rate is shown with decimals; values whole.
const usdFmt = (n) =>
  n == null ? "—" : "$" + new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(Number(n)));
const inrFmt = (n) =>
  n == null ? "—" : "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(Number(n)));

/** Show just the date portion from an ISO timestamp or date-only string, consistently. */
function fmtDateOnly(stored) {
  if (!stored) return "—";
  const d = new Date(stored.includes("T") ? stored : `${stored}T00:00:00`);
  if (Number.isNaN(d.getTime())) return stored.slice(0, 10);
  return d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric", month: "short", day: "2-digit" });
}

/** Format a stored cutover datetime (UTC ISO or legacy date-only) for local display. */
function fmtDatetime(stored) {
  if (!stored) return "—";
  if (!stored.includes("T")) return stored; // legacy date-only
  const d = new Date(stored);
  if (Number.isNaN(d.getTime())) return stored;
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Kpi({ icon: Icon, label, value, hint, tone = "zinc", testid, display }) {
  const toneMap = {
    zinc: "text-zinc-700 bg-zinc-50",
    emerald: "text-emerald-700 bg-emerald-50",
    amber: "text-amber-700 bg-amber-50",
    rose: "text-rose-700 bg-rose-50",
    sky: "text-sky-700 bg-sky-50",
    violet: "text-violet-700 bg-violet-50",
  };
  return (
    <div className="sf-surface p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
        <span className={`p-1 rounded ${toneMap[tone]}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        {label}
      </div>
      <div className="text-2xl font-semibold tracking-tight">{display != null ? display : fmt(value)}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function InventoryDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [negativeOnly, setNegativeOnly] = useState(false);
  const [permissions, setPermissions] = useState([]);

  // Catalog (for the Map dialog product picker)
  const [catalog, setCatalog] = useState([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);

  // Mapping UX
  const [mapDialog, setMapDialog] = useState({ open: false, row: null });
  const [mapTarget, setMapTarget] = useState("");
  const [mapSearch, setMapSearch] = useState("");
  const [mapBusy, setMapBusy] = useState(false);
  const [resolveBusy, setResolveBusy] = useState(false);

  // Drill-down (per-product breakdown)
  const [expanded, setExpanded] = useState({}); // { product_code: { loading, data } }
  // Packing-list viewer popup
  const [plDialog, setPlDialog] = useState({ open: false, pl: null, url: null, loading: false });

  const toggleExpand = async (code) => {
    if (expanded[code]) {
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      return;
    }
    setExpanded((prev) => ({ ...prev, [code]: { loading: true, data: null } }));
    try {
      const res = await axios.get(`${API}/inventory/product/${encodeURIComponent(code)}/breakdown`, auth());
      setExpanded((prev) => ({ ...prev, [code]: { loading: false, data: res.data } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load breakdown");
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
    }
  };

  const openPackingList = (row) => {
    if (!row.packing_list_id) {
      toast.error("No packing list linked to this dispatch line");
      return;
    }
    const token = localStorage.getItem("token");
    const url = `${API}/packing-lists/${row.packing_list_id}/preview?token=${encodeURIComponent(token)}`;
    setPlDialog({ open: true, pl: row, url, loading: false });
  };

  const downloadPackingList = async (plId, fileName) => {
    try {
      const res = await axios.get(`${API}/packing-lists/${plId}/download`, auth());
      const url = res.data?.url;
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        a.download = fileName || "packing_list";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const canManage = permissions.includes("inventory.manage");
  const canViewValue = permissions.includes("inventory.view_value");
  const canBackfill = permissions.includes("po.upload");

  // FX / valuation controls
  const [valuesRevealed, setValuesRevealed] = useState(false);
  const [fxBusy, setFxBusy] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [overrideDialog, setOverrideDialog] = useState(false);
  const [overrideRate, setOverrideRate] = useState("");

  const exportExcel = async () => {
    setExportBusy(true);
    try {
      const res = await axios.get(
        `${API}/inventory/current/export.xlsx`,
        { ...auth(), responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `live_inventory_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Inventory exported to Excel");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      setExportBusy(false);
    }
  };

  const refreshFx = async () => {
    setFxBusy(true);
    try {
      await axios.post(`${API}/inventory/fx-rate/refresh`, {}, auth());
      toast.success("Live USD→INR rate refreshed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "FX refresh failed");
    } finally {
      setFxBusy(false);
    }
  };

  const submitOverride = async () => {
    const rate = parseFloat(overrideRate);
    if (!rate || rate <= 0) {
      toast.error("Enter a valid positive rate");
      return;
    }
    setFxBusy(true);
    try {
      await axios.post(`${API}/inventory/fx-rate/override`, { rate }, auth());
      toast.success(`Pinned USD→INR rate at ${rate}`);
      setOverrideDialog(false);
      setOverrideRate("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Override failed");
    } finally {
      setFxBusy(false);
    }
  };

  const clearOverride = async () => {
    setFxBusy(true);
    try {
      await axios.delete(`${API}/inventory/fx-rate/override`, auth());
      toast.success("Reverted to live market rate");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clear failed");
    } finally {
      setFxBusy(false);
    }
  };

  const runBackfill = async () => {
    setBackfillBusy(true);
    try {
      const r = await axios.post(`${API}/po/backfill-prices`, {}, auth());
      const d = r.data || {};
      toast.success(`Prices backfilled — ${d.lines_priced} line(s) priced across ${d.pos_updated} PO(s)`, { duration: 6000 });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfillBusy(false);
    }
  };

  const load = async () => {
    setRefreshing(true);
    try {
      const [perms, cur] = await Promise.all([
        axios.get(`${API}/auth/me/permissions`, auth()).catch(() => ({ data: { effective_permissions: [] } })),
        axios.get(`${API}/inventory/current?with_value=1`, auth()),
      ]);
      setPermissions(perms.data.effective_permissions || []);
      setData(cur.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load inventory");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadCatalog = async () => {
    if (catalogLoaded) return;
    try {
      const r = await axios.get(`${API}/catalog/products`, auth());
      setCatalog(r.data?.products || []);
      setCatalogLoaded(true);
    } catch (err) {
      toast.error("Failed to load catalog");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openMapDialog = async (row) => {
    setMapDialog({ open: true, row });
    setMapTarget("");
    setMapSearch("");
    await loadCatalog();
  };

  const submitMap = async () => {
    if (!mapTarget || !mapDialog.row) return;
    setMapBusy(true);
    try {
      const r = await axios.post(
        `${API}/inventory/map-product`,
        { raw_text: mapDialog.row.product_code, target_product_code: mapTarget },
        auth(),
      );
      toast.success(`Mapped to ${mapTarget} · ${r.data.lines_updated} dispatch line(s) updated`);
      setMapDialog({ open: false, row: null });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mapping failed");
    } finally {
      setMapBusy(false);
    }
  };

  const handleAutoResolve = async () => {
    setResolveBusy(true);
    try {
      const r = await axios.post(`${API}/inventory/resolve-unmatched`, {}, auth());
      const d = r.data || {};
      toast.success(
        `Auto-resolve done — ${d.codes_resolved}/${d.unmatched_codes_processed} unmatched codes mapped, ${d.lines_updated} lines updated`,
        { duration: 6000 },
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auto-resolve failed");
    } finally {
      setResolveBusy(false);
    }
  };

  const filteredCatalog = useMemo(() => {
    const s = mapSearch.trim().toLowerCase();
    if (!s) return catalog.slice(0, 50);
    return catalog
      .filter(
        (p) =>
          (p.product_code || "").toLowerCase().includes(s) ||
          (p.description || "").toLowerCase().includes(s),
      )
      .slice(0, 100);
  }, [catalog, mapSearch]);

  // Suspicious alias cleanup (post-deploy migration helper)
  const [suspectDialog, setSuspectDialog] = useState({ open: false, items: [], loading: false });
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const openSuspectDialog = async () => {
    setSuspectDialog({ open: true, items: [], loading: true });
    try {
      const r = await axios.get(`${API}/inventory/suspicious-aliases`, auth());
      setSuspectDialog({ open: true, items: r.data?.items || [], loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
      setSuspectDialog({ open: false, items: [], loading: false });
    }
  };

  const confirmCleanup = async () => {
    setCleanupBusy(true);
    try {
      const r = await axios.post(`${API}/inventory/cleanup-suspicious-aliases`, { product_ids: [] }, auth());
      const d = r.data || {};
      toast.success(`Cleaned ${d.aliases_removed} description-like aliases from ${d.products_cleaned} catalog product(s)`);
      setSuspectDialog({ open: false, items: [], loading: false });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  const cutoverActive = !!data?.cutover_date;
  const showValue = !!data?.valued;
  const fx = data?.fx;
  const colCount = 10 + (showValue ? 2 : 0);
  const items = data?.items || [];
  const totals = data?.totals || {};

  const negativeCount = items.filter((r) => (r.live_pieces || 0) < 0).length;

  const filtered = items.filter((r) => {
    if (negativeOnly && (r.live_pieces || 0) >= 0) return false;
    if (!search.trim()) return true;
    const s = search.trim().toLowerCase();
    return (
      (r.product_code || "").toLowerCase().includes(s) ||
      (r.description || "").toLowerCase().includes(s)
    );
  });

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Inventory" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Inventory", active: true, depth: 1 },
        { label: "Cutover Setup", to: "/safety/inventory/setup", depth: 2 },
        { label: "Pallets", to: "/safety/pallets", depth: 1 },
        { label: "Invoice cum Packing Lists", to: "/safety/packing-lists", depth: 1 },
      ]}
    >
      <Toaster richColors position="top-right" />
      <div data-testid="inventory-dashboard-page">
        {/* Header */}
        <div className="sf-surface p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Boxes className="w-5 h-5 text-emerald-600" />
            <div>
              <h1 className="text-xl font-bold tracking-tight">Inventory Dashboard</h1>
              <p className="text-xs text-zinc-500">
                Live Inventory = Cutover Opening Balance + Scan-produced (post-cutover) − Packing-list dispatch (post-cutover) ± Manual adjustments.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={refreshing}
              data-testid="inv-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportExcel}
              disabled={exportBusy || refreshing}
              title="Download the current live inventory as an Excel file"
              data-testid="inv-export-excel-btn"
            >
              <FileSpreadsheet className={`w-4 h-4 mr-1.5 ${exportBusy ? "animate-pulse" : ""}`} />
              Export Excel
            </Button>
            {canManage && (
              <Button
                variant="outline"
                size="sm"
                onClick={openSuspectDialog}
                title="Find catalog aliases that look like descriptions (legacy migration helper)"
                data-testid="inv-clean-aliases-btn"
              >
                <AlertCircle className="w-4 h-4 mr-1.5" />
                Clean aliases
              </Button>
            )}
            {canManage && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAutoResolve}
                disabled={resolveBusy}
                title="Re-run catalog resolution on all unmatched dispatch lines"
                data-testid="inv-auto-resolve-btn"
              >
                <Wand2 className={`w-4 h-4 mr-1.5 ${resolveBusy ? "animate-spin" : ""}`} />
                Auto-resolve
              </Button>
            )}
            {canBackfill && (
              <Button
                variant="outline"
                size="sm"
                onClick={runBackfill}
                disabled={backfillBusy}
                title="Re-parse all PO files to capture USD unit prices for inventory valuation"
                data-testid="inv-backfill-prices-btn"
              >
                <Download className={`w-4 h-4 mr-1.5 ${backfillBusy ? "animate-spin" : ""}`} />
                Backfill prices
              </Button>
            )}
            {canManage && (
              <Button
                size="sm"
                onClick={() => navigate("/safety/inventory/setup")}
                data-testid="inv-setup-btn"
              >
                <Settings2 className="w-4 h-4 mr-1.5" />
                Cutover Setup
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/safety/inventory/reconcile")}
              data-testid="inv-reconcile-btn"
            >
              <Scale className="w-4 h-4 mr-1.5" />
              Reconcile
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/safety/inventory/stock-count")}
              data-testid="inv-stock-count-btn"
            >
              <ScanLine className="w-4 h-4 mr-1.5" />
              Stock Count (scan)
            </Button>
          </div>
        </div>

        {/* Cutover banner */}
        {!loading && !cutoverActive && (
          <div className="sf-surface p-4 mb-4 border-l-4 border-amber-500" data-testid="inv-no-cutover-banner">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-sm">No active Cutover Opening Balance yet</div>
                <p className="text-xs text-zinc-600 mt-1">
                  Live Inventory is currently a partial estimate. Set a Cutover date and upload your physical count to get accurate
                  per-product inventory.
                </p>
              </div>
              {canManage && (
                <Button size="sm" onClick={() => navigate("/safety/inventory/setup")}>
                  Set up now <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        )}

        {cutoverActive && (
          <div className="sf-surface p-3 mb-4 text-xs flex items-center gap-3 flex-wrap" data-testid="inv-cutover-banner">
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
              Cutover v{data.cutover_revision_no}
            </Badge>
            <span className="text-zinc-600">
              Active since <span className="font-medium">{fmtDatetime(data.cutover_date)}</span> — Live Inventory below counts scan + dispatch movements after this moment.
            </span>
          </div>
        )}

        {/* Unresolved rows banner */}
        {!loading && (items.filter((r) => r.catalog_resolved === false).length > 0) && (
          <div className="sf-surface p-3 mb-4 border-l-4 border-amber-500 text-xs flex items-center gap-3 flex-wrap" data-testid="inv-unresolved-banner">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span className="text-zinc-700">
              <span className="font-medium">{items.filter((r) => r.catalog_resolved === false).length} row(s)</span> use descriptions that don't match the catalog — click the
              <span className="inline-flex items-center gap-0.5 px-1 mx-1 rounded bg-amber-100 text-amber-800 text-[10px] border border-amber-200"><Link2 className="w-2.5 h-2.5" /> Map</span>
              button on each row to link them to a catalog product code (saved as an alias for future re-parses).
            </span>
            {canManage && (
              <Button size="sm" variant="ghost" className="h-7 text-xs ml-auto" onClick={handleAutoResolve} disabled={resolveBusy}>
                <Wand2 className={`w-3.5 h-3.5 mr-1 ${resolveBusy ? "animate-spin" : ""}`} />
                Try auto-resolve
              </Button>
            )}
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
          <Kpi icon={Package} label="Opening (cutover)" value={totals.opening_pieces} tone="sky" testid="inv-kpi-opening" />
          <Kpi icon={TrendingUp} label="Produced (post-cutover)" value={totals.produced_pieces} tone="emerald" testid="inv-kpi-produced" />
          <Kpi icon={TrendingDown} label="Dispatched (post-cutover)" value={totals.dispatched_pieces} tone="amber" testid="inv-kpi-dispatched" />
          <Kpi icon={Settings2} label="Adjustments" value={totals.adjustment_pieces} tone="zinc" testid="inv-kpi-adjustments" />
          <Kpi icon={Boxes} label="Live Inventory" value={totals.live_pieces} tone="rose" hint="Pieces on hand" testid="inv-kpi-live" />
          <Kpi icon={Scale} label="Total Weight" value={totals.total_weight_kg} display={`${fmt(totals.total_weight_kg)} kg`} tone="violet"
            hint={totals.unweighed_count ? `${totals.unweighed_count} product(s) no weight` : "Net weight (BOM)"} testid="inv-kpi-weight" />
        </div>

        {/* Valuation (gated by inventory.view_value — backend returns value fields only when permitted) */}
        {showValue && (
          <div className="sf-surface p-4 mb-4" data-testid="inv-valuation-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-600" />
                <h2 className="text-sm font-semibold tracking-tight">Inventory Valuation</h2>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => setValuesRevealed((v) => !v)}
                  data-testid="inv-value-reveal-btn"
                  title={valuesRevealed ? "Hide values" : "Show values"}
                >
                  {valuesRevealed ? <EyeOff className="w-3.5 h-3.5 mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
                  {valuesRevealed ? "Hide" : "Show"} values
                </Button>
                {valuesRevealed && (
                <>
                <Badge variant="outline" className="text-[11px] font-mono" data-testid="inv-fx-rate">
                  ₹{Number(fx?.rate || 0).toFixed(2)} / $1
                </Badge>
                {fx?.is_override && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[11px]">Manual rate</Badge>
                )}
                {fx?.is_stale && !fx?.is_override && (
                  <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[11px]">Stale rate</Badge>
                )}
                <span className="text-[11px] text-zinc-500">
                  {fx?.is_override ? "pinned" : "live"} · updated {fmtDatetime(fx?.updated_at)}
                </span>
                </>
                )}
              </div>
              {canManage && valuesRevealed && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={refreshFx} disabled={fxBusy} data-testid="inv-fx-refresh-btn">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fxBusy ? "animate-spin" : ""}`} />
                    Refresh rate
                  </Button>
                  {fx?.is_override ? (
                    <Button variant="outline" size="sm" onClick={clearOverride} disabled={fxBusy} data-testid="inv-fx-clear-btn">
                      Use live rate
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => { setOverrideRate(fx?.rate ? String(fx.rate) : ""); setOverrideDialog(true); }} data-testid="inv-fx-override-btn">
                      Set manual rate
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Kpi icon={DollarSign} label="Total Value (USD)" value={totals.value_usd} display={valuesRevealed ? usdFmt(totals.value_usd) : VALUE_MASK} tone="emerald" testid="inv-kpi-value-usd"
                hint={valuesRevealed ? (totals.unpriced_count ? `${totals.unpriced_count} product(s) unpriced — excluded` : `${totals.priced_count} product(s) priced`) : "Hidden — click Show values"} />
              <Kpi icon={IndianRupee} label="Total Value (INR)" value={totals.value_inr} display={valuesRevealed ? inrFmt(totals.value_inr) : VALUE_MASK} tone="amber" testid="inv-kpi-value-inr"
                hint={valuesRevealed ? `@ ₹${Number(fx?.rate || 0).toFixed(2)} per $1` : "Hidden"} />
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">
              Value = Live pieces × PO unit price (USD). INR converted at the {fx?.is_override ? "pinned" : "latest live market"} rate. Unpriced products are excluded from totals.
            </p>
          </div>
        )}

        {/* Search + Table */}
        <div className="sf-surface p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search code or description…"
                className="pl-9"
                data-testid="inv-search"
              />
            </div>
            <Button
              variant={negativeOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setNegativeOnly((v) => !v)}
              className={negativeOnly ? "bg-rose-600 hover:bg-rose-700" : ""}
              title="Show only products with a negative live balance"
              data-testid="inv-negative-filter-btn"
            >
              <TrendingDown className="w-4 h-4 mr-1.5" />
              Negative only
              {negativeCount > 0 && (
                <span
                  className={`ml-1.5 px-1.5 rounded-full text-[10px] font-semibold ${negativeOnly ? "bg-white/25 text-white" : "bg-rose-100 text-rose-700"}`}
                >
                  {negativeCount}
                </span>
              )}
            </Button>
            <span className="text-xs text-zinc-500" data-testid="inv-row-count">
              {filtered.length} of {items.length} row{items.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="overflow-x-auto rounded border bg-white">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center text-zinc-500">
                <Boxes className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No inventory rows found</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600">
                  <tr>
                    <th className="px-2 py-2 w-8"></th>
                    <th className="px-3 py-2 text-left font-medium w-32">Code</th>
                    <th className="px-3 py-2 text-left font-medium">Description</th>
                    <th className="px-3 py-2 text-right font-medium">Opening</th>
                    <th className="px-3 py-2 text-right font-medium">+ Produced</th>
                    <th className="px-3 py-2 text-right font-medium">− Dispatched</th>
                    <th className="px-3 py-2 text-right font-medium">± Adj.</th>
                    <th className="px-3 py-2 text-right font-medium">Live</th>
                    <th className="px-3 py-2 text-right font-medium">Wt/pc (kg)</th>
                    <th className="px-3 py-2 text-right font-medium">Total Wt (kg)</th>
                    {showValue && <th className="px-3 py-2 text-right font-medium">Value (USD)</th>}
                    {showValue && <th className="px-3 py-2 text-right font-medium">Value (INR)</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const unresolved = r.catalog_resolved === false;
                    const isOpen = !!expanded[r.product_code];
                    const exp = expanded[r.product_code];
                    return (
                      <Fragment key={`${r.product_code}-${i}`}>
                      <tr
                        className={`border-t hover:bg-zinc-50 cursor-pointer ${unresolved ? "bg-amber-50/50" : ""} ${isOpen ? "bg-zinc-50" : ""}`}
                        data-testid={`inv-row-${i}`}
                        onClick={() => toggleExpand(r.product_code)}
                      >
                        <td className="px-2 py-2 text-zinc-400">
                          <button
                            className="p-0.5 hover:text-zinc-700"
                            data-testid={`inv-expand-${i}`}
                            aria-label="Toggle breakdown"
                          >
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-3 py-2" onClick={(e) => unresolved && e.stopPropagation()}>
                          {unresolved ? (
                            <button
                              onClick={() => openMapDialog(r)}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 text-[11px] font-medium border border-amber-200"
                              title="This row's product code doesn't match the catalog. Click to map."
                              data-testid={`inv-map-${i}`}
                            >
                              <Link2 className="w-3 h-3" /> Map
                            </button>
                          ) : (
                            <span className="font-mono text-zinc-800">{r.product_code}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {unresolved ? (
                            <span className="text-zinc-700" title="Original text from packing list — needs mapping">
                              {r.product_code}
                            </span>
                          ) : (
                            <span className="text-zinc-700">{r.description || "—"}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-sky-700">{fmt(r.opening_pieces)}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">+{fmt(r.produced_pieces)}</td>
                        <td className="px-3 py-2 text-right text-amber-700">−{fmt(r.dispatched_pieces)}</td>
                        <td className="px-3 py-2 text-right">{r.adjustment_pieces > 0 ? "+" : ""}{fmt(r.adjustment_pieces)}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${(r.live_pieces || 0) < 0 ? "text-rose-600" : ""}`}>{fmt(r.live_pieces)}</td>
                        <td className="px-3 py-2 text-right text-zinc-600" data-testid={`inv-wtpc-${i}`}>
                          {r.weighed ? r.net_weight_per_piece : <span className="text-zinc-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right" data-testid={`inv-weight-${i}`}>
                          {r.weighed ? (
                            <span className="font-medium text-violet-700">{fmt(r.total_weight_kg)} kg</span>
                          ) : (
                            <Badge variant="outline" className="text-[10px] text-zinc-500 border-zinc-200 bg-zinc-50">no weight</Badge>
                          )}
                        </td>
                        {showValue && (
                          <td className="px-3 py-2 text-right" data-testid={`inv-value-usd-${i}`}>
                            {r.priced ? (
                              valuesRevealed
                                ? <span className="font-medium text-emerald-700">{usdFmt(r.value_usd)}</span>
                                : <span className="text-zinc-400 select-none">{VALUE_MASK}</span>
                            ) : (
                              <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-200 bg-amber-50">no price</Badge>
                            )}
                          </td>
                        )}
                        {showValue && (
                          <td className="px-3 py-2 text-right" data-testid={`inv-value-inr-${i}`}>
                            {r.priced ? (
                              valuesRevealed
                                ? <span className="font-medium text-amber-700">{inrFmt(r.value_inr)}</span>
                                : <span className="text-zinc-400 select-none">{VALUE_MASK}</span>
                            ) : <span className="text-zinc-300">—</span>}
                          </td>
                        )}
                      </tr>
                      {isOpen && (
                        <tr className="border-t bg-zinc-50/60">
                          <td colSpan={colCount} className="px-4 py-3" data-testid={`inv-breakdown-${i}`}>
                            {exp?.loading ? (
                              <div className="flex items-center gap-2 text-sm text-zinc-500 py-3">
                                <Loader2 className="w-4 h-4 animate-spin" /> Loading breakdown…
                              </div>
                            ) : (
                              <ProductBreakdown
                                data={exp?.data}
                                onOpenReport={(rid) => navigate(`/safety/production-reports/${rid}`)}
                                onOpenPackingList={openPackingList}
                              />
                            )}
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="bg-zinc-50 font-medium border-t-2 border-zinc-300">
                  <tr>
                    <td colSpan={3} className="px-3 py-2 text-right text-zinc-600">Totals</td>
                    <td className="px-3 py-2 text-right text-sky-700">{fmt(totals.opening_pieces)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">+{fmt(totals.produced_pieces)}</td>
                    <td className="px-3 py-2 text-right text-amber-700">−{fmt(totals.dispatched_pieces)}</td>
                    <td className="px-3 py-2 text-right">{totals.adjustment_pieces > 0 ? "+" : ""}{fmt(totals.adjustment_pieces)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(totals.live_pieces)}</td>
                    <td className="px-3 py-2 text-right text-zinc-400"></td>
                    <td className="px-3 py-2 text-right font-semibold text-violet-700" data-testid="inv-total-weight">{fmt(totals.total_weight_kg)} kg</td>
                    {showValue && <td className="px-3 py-2 text-right font-semibold text-emerald-700">{valuesRevealed ? usdFmt(totals.value_usd) : VALUE_MASK}</td>}
                    {showValue && <td className="px-3 py-2 text-right font-semibold text-amber-700">{valuesRevealed ? inrFmt(totals.value_inr) : VALUE_MASK}</td>}
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Map to catalog dialog */}
      <Dialog open={mapDialog.open} onOpenChange={(o) => !o && !mapBusy && setMapDialog({ open: false, row: null })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="inv-map-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-amber-600" />
              Map to catalog product
            </DialogTitle>
            <DialogDescription>
              Link this description to its canonical catalog product. The text will be saved as an alias on the chosen
              product, so future packing-list parses resolve it automatically.
            </DialogDescription>
          </DialogHeader>
          {mapDialog.row && (
            <div className="space-y-3">
              <div className="rounded border bg-amber-50 p-3">
                <div className="text-[11px] uppercase tracking-wide text-amber-700 font-medium mb-1">Unmatched text</div>
                <div className="font-mono text-sm text-zinc-900">{mapDialog.row.product_code}</div>
                <div className="text-xs text-zinc-600 mt-1">
                  Dispatched: {fmt(mapDialog.row.dispatched_pieces)} pieces
                </div>
              </div>

              <div>
                <div className="text-xs text-zinc-500 mb-1.5">Search catalog products</div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input
                    value={mapSearch}
                    onChange={(e) => setMapSearch(e.target.value)}
                    placeholder="Search by code or description…"
                    className="pl-9"
                    data-testid="inv-map-search"
                    autoFocus
                  />
                </div>
              </div>

              <div className="border rounded max-h-[280px] overflow-y-auto">
                {filteredCatalog.length === 0 ? (
                  <div className="p-4 text-center text-xs text-zinc-400">No products match "{mapSearch}"</div>
                ) : (
                  <ul className="divide-y">
                    {filteredCatalog.map((p) => {
                      const sel = mapTarget === p.product_code;
                      return (
                        <li
                          key={p.id}
                          onClick={() => setMapTarget(p.product_code)}
                          className={`p-2 text-sm cursor-pointer hover:bg-zinc-50 ${sel ? "bg-emerald-50" : ""}`}
                          data-testid={`inv-map-cat-${p.product_code}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <span className="font-mono font-medium">{p.product_code}</span>
                              <span className="text-zinc-600 ml-2">{p.description || "—"}</span>
                            </div>
                            {sel && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Selected</Badge>}
                          </div>
                          {(p.aliases || []).length > 0 && (
                            <div className="text-[10px] text-zinc-500 mt-0.5">aliases: {(p.aliases || []).join(", ")}</div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {!catalogLoaded && <div className="text-xs text-zinc-500">Loading catalog…</div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMapDialog({ open: false, row: null })} disabled={mapBusy}>
              Cancel
            </Button>
            <Button onClick={submitMap} disabled={!mapTarget || mapBusy} data-testid="inv-map-confirm-btn">
              {mapBusy ? "Mapping…" : `Map to ${mapTarget || "…"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clean suspicious aliases dialog */}
      <Dialog open={suspectDialog.open} onOpenChange={(o) => !o && !cleanupBusy && setSuspectDialog({ open: false, items: [], loading: false })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="inv-clean-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Clean description-like catalog aliases
            </DialogTitle>
            <DialogDescription>
              Catalog <code>aliases[]</code> should only hold real code variants (e.g., <code>GR-10</code>, <code>GR-10-HT</code>).
              Entries that look like descriptions (long, contain spaces/quotes, mostly letters) shouldn't be there.
              Below are entries flagged as suspicious — click <strong>Clean all</strong> to remove them.
            </DialogDescription>
          </DialogHeader>
          {suspectDialog.loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600 mx-auto" />
            </div>
          ) : suspectDialog.items.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-sm">
              ✅ No suspicious aliases found — catalog is clean.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-zinc-500">
                {suspectDialog.items.length} catalog product(s) have suspicious aliases.
              </div>
              <div className="border rounded max-h-[300px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 text-zinc-600 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">Product</th>
                      <th className="px-2 py-1.5 text-left font-medium">Suspicious aliases</th>
                      <th className="px-2 py-1.5 text-left font-medium">Kept (real code variants)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suspectDialog.items.map((it) => (
                      <tr key={it.product_id} className="border-t">
                        <td className="px-2 py-1.5 align-top">
                          <div className="font-mono">{it.product_code}</div>
                          <div className="text-[10px] text-zinc-500">{it.description}</div>
                        </td>
                        <td className="px-2 py-1.5 align-top text-rose-700">
                          {it.suspicious_aliases.map((a, i) => (
                            <div key={i} className="truncate max-w-[260px]" title={a}>{a}</div>
                          ))}
                        </td>
                        <td className="px-2 py-1.5 align-top text-emerald-700">
                          {it.all_aliases.filter((a) => !it.suspicious_aliases.includes(a)).map((a, i) => (
                            <div key={i} className="truncate max-w-[160px]">{a}</div>
                          )) || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspectDialog({ open: false, items: [], loading: false })} disabled={cleanupBusy}>
              Cancel
            </Button>
            {suspectDialog.items.length > 0 && (
              <Button onClick={confirmCleanup} disabled={cleanupBusy} variant="destructive" data-testid="inv-clean-confirm-btn">
                {cleanupBusy ? "Cleaning…" : `Clean ${suspectDialog.items.reduce((s, it) => s + it.suspicious_aliases.length, 0)} alias(es)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Packing List viewer popup */}
      <Dialog open={plDialog.open} onOpenChange={(o) => !o && setPlDialog({ open: false, pl: null, url: null, loading: false })}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] max-h-[92vh] flex flex-col" data-testid="inv-pl-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-amber-600" />
              Packing List {plDialog.pl?.invoice_no ? `· ${plDialog.pl.invoice_no}` : ""}
            </DialogTitle>
            <DialogDescription>
              {plDialog.pl?.file_name || "Preview"}
              {plDialog.pl?.dispatch_date ? ` · dispatched ${plDialog.pl.dispatch_date}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 border rounded bg-zinc-100">
            {plDialog.url ? (
              <iframe
                src={plDialog.url}
                className="w-full h-full rounded"
                title="Packing List Preview"
                data-testid="inv-pl-iframe"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-zinc-500 text-sm">No preview available</div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPlDialog({ open: false, pl: null, url: null, loading: false })}
            >
              Close
            </Button>
            <Button
              onClick={() => downloadPackingList(plDialog.pl?.packing_list_id, plDialog.pl?.file_name)}
              data-testid="inv-pl-download-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Download original
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* FX manual override dialog */}
      <Dialog open={overrideDialog} onOpenChange={(o) => !o && !fxBusy && setOverrideDialog(false)}>
        <DialogContent className="max-w-sm" data-testid="inv-fx-override-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-amber-600" />
              Set manual USD→INR rate
            </DialogTitle>
            <DialogDescription>
              Pin a specific rate (e.g. your customs/billing rate). This overrides the live market rate for all inventory valuation until you revert.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-zinc-500">Rate (₹ per $1)</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={overrideRate}
              onChange={(e) => setOverrideRate(e.target.value)}
              placeholder="e.g. 83.25"
              data-testid="inv-fx-override-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialog(false)} disabled={fxBusy}>Cancel</Button>
            <Button onClick={submitOverride} disabled={fxBusy} data-testid="inv-fx-override-save-btn">
              {fxBusy ? "Saving…" : "Pin rate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SafetyShell>
  );
}

function ProductBreakdown({ data, onOpenReport, onOpenPackingList }) {
  if (!data) return <div className="text-sm text-zinc-500 py-2">No breakdown data.</div>;
  const { opening = [], produced = [], dispatched = [], adjustments = [], totals = {} } = data;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Produced */}
      <div className="bg-white rounded border" data-testid="bd-produced">
        <div className="px-3 py-2 border-b bg-emerald-50/60 flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-800">
            <Package className="w-4 h-4" /> Produced (pallet scans)
          </span>
          <span className="text-sm font-semibold text-emerald-700">+{fmt(totals.produced_pieces)}</span>
        </div>
        {produced.length === 0 ? (
          <div className="px-3 py-4 text-xs text-zinc-400">No production scans.</div>
        ) : (
          <div className="max-h-60 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 text-zinc-500 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Pallet</th>
                  <th className="px-2 py-1.5 text-right font-medium">Qty</th>
                  <th className="px-2 py-1.5 text-left font-medium">Date</th>
                  <th className="px-2 py-1.5 text-left font-medium">DPR</th>
                </tr>
              </thead>
              <tbody>
                {produced.map((p, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5 font-mono">{p.pallet_id}</td>
                    <td className="px-2 py-1.5 text-right text-emerald-700">{fmt(p.qty_pieces)}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{fmtDateOnly(p.produced_at)}</td>
                    <td className="px-2 py-1.5">
                      {p.report ? (
                        <button
                          onClick={() => onOpenReport(p.report_id)}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 hover:bg-sky-200 text-[11px] font-medium border border-sky-200"
                          data-testid={`bd-dpr-${i}`}
                        >
                          <FileText className="w-3 h-3" /> {p.report?.report_date || p.report?.report_no || "DPR"}
                        </button>
                      ) : (
                        <span className="text-zinc-400 text-[11px]">— no DPR</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dispatched */}
      <div className="bg-white rounded border" data-testid="bd-dispatched">
        <div className="px-3 py-2 border-b bg-amber-50/60 flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-sm font-medium text-amber-800">
            <Truck className="w-4 h-4" /> Dispatched (packing lists)
          </span>
          <span className="text-sm font-semibold text-amber-700">−{fmt(totals.dispatched_pieces)}</span>
        </div>
        {dispatched.length === 0 ? (
          <div className="px-3 py-4 text-xs text-zinc-400">No dispatches.</div>
        ) : (
          <div className="max-h-60 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 text-zinc-500 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Invoice / File</th>
                  <th className="px-2 py-1.5 text-right font-medium">Qty</th>
                  <th className="px-2 py-1.5 text-left font-medium">Date</th>
                  <th className="px-2 py-1.5 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {dispatched.map((d, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5">
                      <div className="font-medium text-zinc-800">{d.invoice_no || "—"}</div>
                      <div className="text-[10px] text-zinc-500 truncate max-w-[220px]" title={d.file_name}>{d.file_name || ""}</div>
                    </td>
                    <td className="px-2 py-1.5 text-right text-amber-700">{fmt(d.qty_pieces)}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{fmtDateOnly(d.dispatch_date)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        onClick={() => onOpenPackingList(d)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 hover:bg-zinc-200 text-[11px] font-medium border"
                        data-testid={`bd-pl-view-${i}`}
                        title="View packing list"
                      >
                        <ClipboardList className="w-3 h-3" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Opening */}
      {opening.length > 0 && (
        <div className="bg-white rounded border" data-testid="bd-opening">
          <div className="px-3 py-2 border-b bg-sky-50/60 flex items-center justify-between">
            <span className="text-sm font-medium text-sky-800">Opening (cutover baseline)</span>
            <span className="text-sm font-semibold text-sky-700">{fmt(totals.opening_pieces)}</span>
          </div>
          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-xs">
              <tbody>
                {opening.map((o, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5 font-mono">{o.po_number || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{o.customer_name || ""}</td>
                    <td className="px-2 py-1.5 text-right text-sky-700">{fmt(o.qty_pieces)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Adjustments */}
      {adjustments.length > 0 && (
        <div className="bg-white rounded border" data-testid="bd-adjustments">
          <div className="px-3 py-2 border-b bg-zinc-50 flex items-center justify-between">
            <span className="text-sm font-medium text-zinc-700">Manual Adjustments</span>
            <span className="text-sm font-semibold">{totals.adjustment_pieces > 0 ? "+" : ""}{fmt(totals.adjustment_pieces)}</span>
          </div>
          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-xs">
              <tbody>
                {adjustments.map((a, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5 text-right font-medium">{a.qty_delta > 0 ? "+" : ""}{fmt(a.qty_delta)}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{a.reason || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-500">{a.effective_date || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryDashboard;
